import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { RouteDetail } from '../../types/reactions';

interface RouteDrawerProps {
  routeId: number;
  reactionId: number;
  onClose: () => void;
}

const svgCache = new Map<string, string>();

const StepThumbnail: React.FC<{ smiles: string }> = ({ smiles }) => {
  const [svg, setSvg] = useState<string | null>(null);
  const cacheKey = `route-thumb|${smiles}`;
  const cached = svgCache.get(cacheKey);

  useEffect(() => {
    if (cached || !smiles) return;
    let cancelled = false;
    const fetchSvg = async () => {
      try {
        const { default: apiClient } = await import('../../api/client');
        const res = await apiClient.post('/v2/indigo/render', {
          structure: { value: smiles, type: 'RAW' },
          width: '280px',
          height: '64px',
        }, { responseType: 'text' });
        if (!cancelled) {
          svgCache.set(cacheKey, res.data);
          setSvg(res.data);
        }
      } catch {
        // silent
      }
    };
    fetchSvg();
    return () => { cancelled = true; };
  }, [smiles, cacheKey, cached]);

  const displaySvg = cached || svg;

  if (displaySvg) {
    return (
      <div
        className="w-full h-16 bg-white/5 rounded overflow-hidden [&>svg]:max-w-full [&>svg]:max-h-full [&>svg]:mx-auto"
        dangerouslySetInnerHTML={{ __html: displaySvg }}
      />
    );
  }
  return (
    <div className="w-full h-16 bg-white/5 rounded flex items-center justify-center">
      <div className="w-3 h-3 border-2 border-slate-600 border-t-teal-400 rounded-full animate-spin" />
    </div>
  );
};

const RouteDrawer: React.FC<RouteDrawerProps> = ({ routeId, reactionId, onClose }) => {
  const navigate = useNavigate();
  const [route, setRoute] = useState<RouteDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const fetchRoute = async () => {
      try {
        const { default: apiClient } = await import('../../api/client');
        const res = await apiClient.get(`/v2/reactions/${reactionId}/route-detail`, {
          params: { route_id: routeId },
        });
        if (!cancelled && res.data) setRoute(res.data);
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRoute();
    return () => { cancelled = true; };
  }, [routeId, reactionId]);

  // Build a map from step_id to the linking InChIKey (edge going OUT from that step)
  const edgeFromStep = new Map<number, string>();
  if (route) {
    for (const edge of route.edges) {
      edgeFromStep.set(edge.from_step_id, edge.inchi_key);
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed top-0 right-0 bottom-0 w-[350px] bg-slate-900 text-white z-50 shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <div className="flex items-center gap-2 min-w-0">
            <span className="shrink-0 rounded bg-teal-500/20 px-2 py-0.5 text-xs font-semibold text-teal-300">
              Route
            </span>
            <span className="text-sm font-medium truncate text-slate-200">
              {route?.name || `Route #${routeId}`}
            </span>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center hover:bg-slate-700 transition-colors text-slate-400 hover:text-white"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-slate-600 border-t-teal-400 rounded-full animate-spin" />
            </div>
          )}

          {!loading && !route && (
            <p className="text-sm text-slate-400 text-center py-8">Failed to load route</p>
          )}

          {!loading && route && (
            <div className="space-y-0">
              {route.steps.map((step, idx) => {
                const isCurrent = step.reaction_id === reactionId;
                const linkingIk = edgeFromStep.get(step.step_id);
                const isLast = idx === route.steps.length - 1;

                return (
                  <div key={step.step_id}>
                    {/* Step card */}
                    <button
                      onClick={() => {
                        if (!isCurrent) {
                          navigate(`/chemistry/reactions/${step.reaction_id}`);
                          onClose();
                        }
                      }}
                      className={`w-full text-left rounded-lg p-3 transition-all ${
                        isCurrent
                          ? 'bg-teal-500/15 border border-teal-500/40 ring-1 ring-teal-500/20'
                          : 'bg-slate-800/60 border border-slate-700/50 hover:bg-slate-800 hover:border-slate-600 cursor-pointer'
                      }`}
                    >
                      {/* Step header */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          isCurrent
                            ? 'bg-teal-500 text-white'
                            : 'bg-slate-700 text-slate-300'
                        }`}>
                          {step.step_number}
                        </span>
                        <span className={`text-sm font-medium truncate ${
                          isCurrent ? 'text-teal-200' : 'text-slate-300'
                        }`}>
                          {step.notes || `Reaction #${step.reaction_id}`}
                        </span>
                        {step.yield_percent != null && (
                          <span className={`ml-auto shrink-0 px-2 py-0.5 rounded text-xs font-semibold ${
                            isCurrent
                              ? 'bg-teal-500/20 text-teal-300'
                              : 'bg-emerald-500/15 text-emerald-400'
                          }`}>
                            {step.yield_percent}%
                          </span>
                        )}
                      </div>

                      {/* Reaction thumbnail */}
                      {step.reaction_smiles && (
                        <StepThumbnail smiles={step.reaction_smiles} />
                      )}

                      {isCurrent && (
                        <div className="mt-2 text-[10px] text-teal-400 font-medium uppercase tracking-wider">
                          Current step
                        </div>
                      )}
                    </button>

                    {/* Connecting edge / linking molecule */}
                    {!isLast && (
                      <div className="flex items-stretch ml-6 my-0">
                        {/* Vertical line */}
                        <div className="w-px bg-slate-600 mx-auto" style={{ minHeight: '36px' }} />
                        {/* Linking InChIKey label */}
                        {linkingIk && (
                          <div className="flex items-center ml-3">
                            <svg className="w-3 h-3 text-slate-500 shrink-0 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.102 1.101" />
                            </svg>
                            <span className="text-[10px] font-mono text-slate-500 truncate max-w-[200px]" title={linkingIk}>
                              {linkingIk.substring(0, 14)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {route && (
          <div className="px-4 py-3 border-t border-slate-700 text-xs text-slate-500">
            <div className="flex justify-between">
              <span>{route.steps.length} steps</span>
              <span className="uppercase">{route.status}</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RouteDrawer;
